import { truncateHex } from '@lfg/core/lib';
import { Modal, ModalHandle, ModalProps } from '@lfg/ui/components';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';

import { MemberSchemaType } from 'src/global/schemas/member.schema';
import { colors } from 'src/global/styles/colors';
import { useDeleteMemberMutation, useGetMembersQuery } from 'src/store/services/member.service';
import { useAppSelector } from 'src/store/utils';

import { SecondaryButton } from '../Buttons/SecondaryButton';

export interface AccessControlListModalHandle extends ModalHandle {}

export interface AccessControlListModalProps extends Omit<ModalProps, 'children'> {
  projectId: string;
  onEditMember: (member: MemberSchemaType) => void;
  onAddMember: () => void;
}

const AccessControlListModal = forwardRef<AccessControlListModalHandle, AccessControlListModalProps>(
  ({ projectId, onEditMember, onAddMember, ...rest }, ref) => {
    const modalRef = useRef<ModalHandle>(null);
    const { address } = useAccount();
    const { network } = useAppSelector(state => state.app);

    const {
      data: members = [],
      isLoading,
      error,
    } = useGetMembersQuery(
      {
        programId: projectId,
        address: address as string,
        network,
      },
      { skip: !projectId || !address },
    );

    const [deleteMember, { isLoading: isDeleting }] = useDeleteMemberMutation();

    const onShow = useCallback(() => modalRef.current?.show(), []);
    const onHide = useCallback(() => modalRef.current?.hide(), []);

    useImperativeHandle(ref, () => ({ show: onShow, hide: onHide }), [onShow, onHide]);

    const handleDeleteMember = async (memberAddr: string) => {
      try {
        await deleteMember({
          programId: projectId,
          memberAddr,
          address: address as string,
          network,
        }).unwrap();
        toast.success('Member removed successfully');
      } catch (error) {
        console.error('Failed to delete member:', error);
        toast.error('Failed to remove member');
      }
    };

    const renderMemberRow = (member: MemberSchemaType, index: number) => {
      const isMe = member.addr?.toLowerCase() === address?.toLocaleLowerCase();
      return (
        <Box
          key={member.addr}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '4px 0px',
            borderRadius: '4px',
            minHeight: '48px',
          }}>
          <Box
            sx={{
              padding: '8px 16px',
              width: '160px',
              lineHeight: '24px',
              border: `1px solid ${colors.neutral06}`,
              borderRadius: 1,
            }}>
            <Typography
              fontSize='14px'
              fontWeight={400}
              color={colors.white}
              textOverflow={'ellipsis'}
              overflow={'hidden'}
              whiteSpace={'nowrap'}>
              {member.name}
            </Typography>
          </Box>

          <Box
            sx={{
              padding: '8px 16px',
              width: '160px',
              lineHeight: '24px',
              border: `1px solid ${colors.neutral06}`,
              borderRadius: 1,
            }}>
            <Typography fontSize='14px' fontWeight={400} color={colors.white}>
              {truncateHex(member.addr, 4)}
            </Typography>
          </Box>

          <Box
            sx={{
              padding: '8px 16px',
              width: '136px',
              lineHeight: '24px',
              border: `1px solid ${colors.neutral06}`,
              borderRadius: 1,
            }}>
            <Typography fontSize='14px' fontWeight={400} color={colors.white}>
              {member.role}
            </Typography>
          </Box>

          {!isMe ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px',
                width: '48px',
                height: '24px',
                cursor: 'pointer',
              }}>
              <IconButton onClick={() => onEditMember(member)}>
                <EditIcon sx={{ width: '20px', height: '20px', color: colors.white }} />
              </IconButton>
              <IconButton onClick={() => handleDeleteMember(member.addr)}>
                <RemoveIcon sx={{ width: '20px', height: '20px', color: colors.white }} />
              </IconButton>
            </Box>
          ) : null}
        </Box>
      );
    };

    return (
      <Modal
        ref={modalRef}
        bgColor={colors.modal}
        sx={{
          maxWidth: '600px',
          width: '100%',
          boxShadow: '0px 6px 10px 4px rgba(0, 0, 0, 0.15), 0px 2px 3px 0px rgba(0, 0, 0, 0.30)',
          border: `1px solid ${colors.neutral015}`,
        }}
        {...rest}>
        <Stack gap={3} flexGrow={1}>
          <Stack gap={2} alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
            <Typography component={'h3'} fontSize={'18px'} fontWeight={'500'} color={'white'}>
              Access Control
            </Typography>
            <CloseIcon onClick={onHide} sx={{ color: 'white', cursor: 'pointer' }} />
          </Stack>

          <Stack gap={1}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '4px 0px',
                backgroundColor: colors.neutral06,
                borderRadius: '4px',
                minHeight: '48px',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px',
                  width: '160px',
                }}>
                <Typography fontSize='14px' fontWeight={400} color={colors.neutral300}>
                  Name
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px',
                  flex: 1,
                }}>
                <Typography fontSize='14px' fontWeight={400} color={colors.neutral300}>
                  Wallet Address
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px',
                  flex: 1,
                }}>
                <Typography fontSize='14px' fontWeight={400} color={colors.neutral300}>
                  Role
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px',
                  width: '24px',
                  height: '24px',
                  opacity: 0,
                }}>
                <EditIcon sx={{ width: '20px', height: '20px' }} />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: '200px' }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                  <CircularProgress size={24} sx={{ color: colors.white }} />
                </Box>
              ) : error ? (
                <Typography fontSize='14px' color={colors.error} textAlign='center' padding='24px'>
                  Failed to load members
                </Typography>
              ) : members.length === 0 ? (
                <Typography fontSize='14px' color={colors.neutral300} textAlign='center' padding='24px'>
                  No members found
                </Typography>
              ) : (
                members.map((member, index) => renderMemberRow(member, index))
              )}
            </Box>
          </Stack>

          <SecondaryButton
            variant='text'
            onClick={onAddMember}
            sx={{
              background: 'transparent',
            }}>
            <AddIcon sx={{ width: '20px', height: '20px' }} />
            Add another
          </SecondaryButton>
        </Stack>
      </Modal>
    );
  },
);

export default AccessControlListModal;
